import { DateTime, type WeekdayNumbers } from "luxon";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { IanaZone, LayoutUtilProps, Locale } from "../../types";
import { CalendarNow } from "./CalendarNowButton";
import { CalendarYearButton } from "./CalendarYearButton";
import { CalendarPrev } from "./CalendarPrevButton";
import { CalendarNext } from "./CalendarNextButton";
import { CalendarMonthButton } from "./CalendarMonthButton";
import { CalendarMonth } from "./CalendarMonth";
import {
  CalendarContext,
  CalendarContextProps,
  CalendarRangeContextProps,
} from "./CalendarContext";
import { useMergeRefs } from "../../hooks";
import { useLayoutPropsUtil } from "../../internal/hooks";
import styles from "./Calendar.module.scss";
import cx from "classnames";
import { normalizeDate } from "./internal/utils";
import { CalendarMonthSelection } from "./internal/CalendarMonthSelection";
import { YearSelector } from "../../internal/components/YearSelector";
import { useCalendarBetaProps } from "../../beta";

export type CalendarValue = string | undefined;

export type CalendarRangeValue = { start?: string; end?: string } | undefined;

const toolbarFocusStates = ["month", "year", "next", "prev", "now"] as const;

/**
 * Props for the Calendar component
 * @extends Omit<ComponentPropsWithoutRef<"div">, "value" | "defaultValue">
 * @extends LayoutUtilProps
 */
type CalendarCommonProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "value" | "defaultValue"
> & {
  /**
   * @deprecated children will be overridden internally
   */
  children?: ComponentPropsWithoutRef<"div">["children"];
  /**
   * The default focused date. Used to determine what month and year is shown.
   */
  defaultFocusedDate?: string | null;
  /**
   * The focused date. Used to determine what month and year is currently shown.
   */
  focusedDate?: string | null;
  /**
   * The minimum date allowed.
   */
  minDate?: string;
  /**
   * The maximum date allowed.
   */
  maxDate?: string;
  /**
   * The days of the week and dates that are unavailable.
   */
  unavailable?: {
    /**
     * The days of the week that are unavailable.
     */
    daysOfWeek?: WeekdayNumbers[];
    /**
     * The specific dates that are unavailable.
     */
    dates?: string[];
  };
  /**
   * The locale to use for the calendar.
   */
  locale?: Locale;
  /**
   * The time zone to use for the calendar.
   */
  defaultTimeZone?: IanaZone;
  /**
   * The day of the week to start the calendar on.
   */
  startDay?: "Sunday" | "Monday";

  /**
   * Whether the calendar should focus on the focused date.
   * @internal - Do not use this prop directly.
   */
  _disableAutofocus?: boolean;
  /** Whether the calendar should set all tab indexes to -1.
   * @internal - Do not use this prop directly.
   */
  _disableFocus?: boolean;
};

type CalendarSingleProps = CalendarCommonProps & {
  /**
   * The function to call when the value changes.
   *
   * @param data - The data to pass to the function.
   */
  onSelection?: (data: { value: CalendarValue }) => void;
  /**
   * The default date value for uncontrolled mode.
   */
  defaultValue?: string | false;
  /**
   * The value for controlled mode.
   */
  value?: string;
  /**
   * Whether the calendar is in range mode.
   */
  range?: false;
};

type CalendarRangeProps = CalendarCommonProps & {
  /**
   * The function to call when the value changes.
   *
   * @param data.value - The date value that is selected.
   */
  onSelection?: (data: { value: CalendarRangeValue }) => void;
  /**
   * The default date value for uncontrolled mode.
   */
  defaultValue?: { start?: string; end?: string };
  /**
   * The date value for controlled mode.
   */
  value?: { start?: string; end?: string };
  /**
   * Whether the calendar is in range mode.
   */
  range: true;
};

export type CalendarProps = LayoutUtilProps &
  (CalendarSingleProps | CalendarRangeProps);

/**
 * Calendar component for viewing and selecting a date or range of dates.
 *
 * Features:
 * - Single date and date range selection modes
 * - Month and year navigation with header buttons
 * - Configurable start day (Sunday or Monday)
 * - Date range restrictions with min/max dates
 * - Unavailable dates and days of week support
 * - Locale and timezone customization
 * - Full accessibility support with ARIA attributes
 * - Keyboard navigation support
 * - Responsive design with automatic width detection
 * - Month and year selection views
 * - Today button for quick navigation
 *
 * @example
 * <Calendar
 *   value="2024-01-15"
 *   onSelection={({ value }) => console.log('Selected:', value)}
 *   minDate="2024-01-01"
 *   maxDate="2024-12-31"
 * />
 */
export const Calendar = Object.assign(
  forwardRef<HTMLDivElement, CalendarProps>(function CalendarInner(props, ref) {
    const uid = useId();
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      children,
      focusedDate: focusProp,
      defaultFocusedDate,
      locale = Intl.DateTimeFormat().resolvedOptions().locale as Locale,
      defaultTimeZone = Intl.DateTimeFormat().resolvedOptions()
        .timeZone as IanaZone,
      startDay = "Sunday",
      minDate,
      maxDate,
      unavailable = {
        dates: [],
        daysOfWeek: [],
      },
      id,
      style,
      value: iValue,
      defaultValue: iDefaultValue,
      onSelection,
      range,
      className,
      _disableAutofocus,
      _disableFocus,
      ...remainingProps
    } = componentProps;

    const { dateMetadata, onMonthView } = useCalendarBetaProps();

    const calendarRef = useRef<HTMLDivElement>(null);
    const combinedRef = useMergeRefs([ref, calendarRef]);
    const lastIValue = useRef(iValue);
    const yearButtonRef = useRef<HTMLButtonElement>(null);
    const monthButtonRef = useRef<HTMLButtonElement>(null);
    const headerRef = useRef<HTMLSpanElement>(null);
    const [toolbarFocus, setToolbarFocus] = useState<
      (typeof toolbarFocusStates)[number]
    >(toolbarFocusStates[0]);

    const [calendarWidth, setCalendarWidth] = useState<number | undefined>(
      undefined,
    );

    const [calendarSelectionState, setCalendarSelectionState] = useState<
      "day" | "month" | "year"
    >("day");

    const visibleHeaderButtons = useMemo(() => {
      if (calendarSelectionState !== "day") {
        return toolbarFocusStates.slice(0, 2);
      }
      return toolbarFocusStates;
    }, [calendarSelectionState]);

    useEffect(() => {
      if (!calendarRef.current || style?.width != null) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          if (calendarSelectionState === "day" && width > 100) {
            setCalendarWidth(width);
          }
        }
      });

      resizeObserver.observe(calendarRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }, [calendarSelectionState, style?.width]);

    // Common
    const styleCombined = {
      width: calendarWidth,
      ...style,
      ...layoutStyles,
    };

    const classNameCombined = cx(styles["calendar"], className);

    const { nDate, nDefaultDate, nToday } = useMemo(
      () => ({
        nDate: iValue
          ? typeof iValue === "string"
            ? normalizeDate(iValue, defaultTimeZone)
            : {
                start: iValue.start
                  ? normalizeDate(iValue.start, defaultTimeZone)
                  : null,
                end: iValue.end
                  ? normalizeDate(iValue.end, defaultTimeZone)
                  : null,
              }
          : null,
        nDefaultDate: iDefaultValue
          ? typeof iDefaultValue === "string"
            ? normalizeDate(iDefaultValue, defaultTimeZone)
            : {
                start: iDefaultValue.start
                  ? normalizeDate(iDefaultValue.start, defaultTimeZone)
                  : null,
                end: iDefaultValue?.end
                  ? normalizeDate(iDefaultValue.end, defaultTimeZone)
                  : null,
              }
          : null,
        nToday:
          typeof iValue === "string"
            ? normalizeDate(DateTime.now().toISO(), defaultTimeZone)
            : {
                start: normalizeDate(DateTime.now().toISO(), defaultTimeZone),
                end: normalizeDate(DateTime.now().toISO(), defaultTimeZone),
              },
      }),
      [iValue, iDefaultValue, defaultTimeZone],
    );

    const [value, setValue] = useState<
      | CalendarContextProps["value"]
      | CalendarRangeContextProps["value"]
      | undefined
    >(nDate ?? nDefaultDate ?? nToday);

    const getInitialDate = useMemo(() => {
      if (defaultFocusedDate) {
        return normalizeDate(defaultFocusedDate, defaultTimeZone);
      }
      if (iDefaultValue) {
        const date =
          typeof iDefaultValue === "string"
            ? normalizeDate(iDefaultValue, defaultTimeZone)
            : normalizeDate(
                iDefaultValue.start ?? iDefaultValue.end ?? "",
                defaultTimeZone,
              );
        return date;
      }
      if (
        !DateTime.isDateTime(nDate) &&
        !DateTime.isDateTime(nDefaultDate) &&
        !DateTime.isDateTime(nToday)
      ) {
        return nDate?.start ?? nDefaultDate?.start ?? nToday?.start;
      }

      return (nDate ?? nDefaultDate ?? nToday) as DateTime;
    }, [
      defaultFocusedDate,
      iDefaultValue,
      nDate,
      nDefaultDate,
      nToday,
      defaultTimeZone,
    ]);

    const initialDate = getInitialDate ?? DateTime.now();

    const [focusedDate, setFocusedDate] = useState<
      | CalendarContextProps["focusedDate"]
      | CalendarRangeContextProps["focusedDate"]
      | undefined
    >(initialDate);

    const [hoveredDate, setHoveredDate] = useState<
      | CalendarContextProps["hoveredDate"]
      | CalendarRangeContextProps["hoveredDate"]
    >();

    const [selectedMonth, setSelectedMonth] = useState<DateTime["month"]>(
      initialDate.month,
    );
    const [selectedYear, setSelectedYear] = useState<DateTime["year"]>(
      initialDate.year,
    );

    const currentVisibleMonth = useMemo(() => {
      return {
        year: focusedDate?.year ?? selectedYear,
        month: focusedDate?.month ?? selectedMonth,
      };
    }, [focusedDate?.month, focusedDate?.year, selectedYear, selectedMonth]);

    useEffect(
      () => onMonthView?.(currentVisibleMonth),
      // eslint-disable-next-line react-hooks/exhaustive-deps -- omit onMonthView from deps because we don't want to trigger infinite re-renders
      [currentVisibleMonth],
    );

    useEffect(() => {
      if (!focusProp && !defaultFocusedDate) return;
      if (focusProp) {
        setFocusedDate(normalizeDate(focusProp, defaultTimeZone));
      } else if (defaultFocusedDate) {
        setFocusedDate(normalizeDate(defaultFocusedDate, defaultTimeZone));
        setSelectedMonth(
          normalizeDate(defaultFocusedDate, defaultTimeZone).month,
        );
        setSelectedYear(
          normalizeDate(defaultFocusedDate, defaultTimeZone).year,
        );
      }
      // defaultTimeZone should not rerender
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusProp, defaultFocusedDate]);

    useEffect(() => {
      if (!iValue) {
        lastIValue.current = iValue;
        return;
      }
      if (typeof iValue === "string") {
        setValue(normalizeDate(iValue, defaultTimeZone));
        setFocusedDate(normalizeDate(iValue, defaultTimeZone));
      } else if (
        iValue.end &&
        (typeof lastIValue.current !== "object" ||
          lastIValue?.current?.end !== iValue.end)
      ) {
        setValue({
          start: iValue.start
            ? normalizeDate(iValue.start, defaultTimeZone)
            : null,
          end: iValue.end ? normalizeDate(iValue.end, defaultTimeZone) : null,
        });
        setFocusedDate(normalizeDate(iValue.end, defaultTimeZone));
      } else if (
        iValue.start &&
        (typeof lastIValue.current !== "object" ||
          lastIValue?.current?.start !== iValue.start)
      ) {
        setFocusedDate(normalizeDate(iValue.start, defaultTimeZone));
      }
      lastIValue.current = iValue;
      // defaultTimeZone should not rerender
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [iValue]);

    // Memoize normalized minDate and maxDate to avoid new references on every render
    const normalizedMinDate = useMemo(
      () => (minDate ? normalizeDate(minDate, defaultTimeZone) : null),
      [minDate, defaultTimeZone],
    );
    const normalizedMaxDate = useMemo(
      () => (maxDate ? normalizeDate(maxDate, defaultTimeZone) : null),
      [maxDate, defaultTimeZone],
    );

    const dateMetadataMap = useMemo(() => {
      if (!dateMetadata) return undefined;
      return new Map(dateMetadata.map((item) => [item.date, item.context]));
    }, [dateMetadata]);

    const providerValue = useMemo(() => {
      // TODO FUTURE: Hookup with dateformat from localization provider
      const format = {
        weekday: "long",
        month: "long",
        day: "2-digit",
        year: "numeric",
      } as const;

      return {
        disableAutofocus: _disableAutofocus ?? false,
        focusedDate,
        setFocusedDate,
        selectedMonth,
        setSelectedMonth,
        selectedYear,
        setSelectedYear,
        hoveredDate,
        setHoveredDate,
        value,
        setValue,
        defaultTimeZone,
        id: id ?? uid,
        onSelection,
        controlled: !!iValue,
        range,
        minDate: normalizedMinDate,
        maxDate: normalizedMaxDate,
        unavailable: {
          dates: new Set(unavailable.dates),
          daysOfWeek: new Set(unavailable.daysOfWeek),
        },
        locale,
        dateFormat: format,
        startDay,
        dateMetadata: dateMetadataMap,
      };
    }, [
      focusedDate,
      selectedMonth,
      selectedYear,
      hoveredDate,
      id,
      locale,
      normalizedMaxDate,
      normalizedMinDate,
      iValue,
      onSelection,
      range,
      defaultTimeZone,
      uid,
      unavailable,
      value,
      startDay,
      dateMetadataMap,
      _disableAutofocus,
    ]);

    const handleHeaderKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        switch (e.key) {
          case "ArrowDown": {
            e.preventDefault();
            if (calendarSelectionState === "day") {
              const focusedDayId = `${id ?? uid}-${focusedDate?.toISODate()}`;
              const root =
                (e.currentTarget.getRootNode() as Document | ShadowRoot) ??
                document;
              root.getElementById(focusedDayId)?.focus();
            }
            break;
          }
          case "ArrowLeft":
            e.preventDefault();
            setToolbarFocus((previousToolbarFocus) => {
              const newToolbarFocus =
                previousToolbarFocus === visibleHeaderButtons[0]
                  ? visibleHeaderButtons[visibleHeaderButtons.length - 1]
                  : (visibleHeaderButtons[
                      visibleHeaderButtons.indexOf(previousToolbarFocus) - 1
                    ] ?? visibleHeaderButtons[visibleHeaderButtons.length - 1]);
              const button = headerRef.current?.querySelector(
                `[data-calendar-header-button-name="${newToolbarFocus}"]`,
              );
              if (
                button &&
                "focus" in button &&
                typeof (button as HTMLElement).focus === "function"
              ) {
                (button as HTMLElement).focus();
              }
              return newToolbarFocus;
            });

            break;
          case "ArrowRight":
            e.preventDefault();
            setToolbarFocus((previousToolbarFocus) => {
              const newToolbarFocus =
                previousToolbarFocus ===
                visibleHeaderButtons[visibleHeaderButtons.length - 1]
                  ? visibleHeaderButtons[0]
                  : (visibleHeaderButtons[
                      visibleHeaderButtons.indexOf(previousToolbarFocus) + 1
                    ] ?? visibleHeaderButtons[0]);
              const button = headerRef.current?.querySelector(
                `[data-calendar-header-button-name="${newToolbarFocus}"]`,
              );
              if (
                button &&
                "focus" in button &&
                typeof (button as HTMLElement).focus === "function"
              ) {
                (button as HTMLElement).focus();
              }
              return newToolbarFocus;
            });
        }
      },
      [visibleHeaderButtons, calendarSelectionState, focusedDate, id, uid],
    );

    const handleMonthSelection = useCallback(
      (month: DateTime["month"]) => {
        monthButtonRef.current?.focus();
        setToolbarFocus(toolbarFocusStates[0]);
        setSelectedMonth(month);
        setFocusedDate(
          DateTime.fromObject(
            {
              year: focusedDate?.year ?? 1,
              month: month,
              day: 1,
            },
            { zone: defaultTimeZone },
          ).startOf("day"),
        );
        setCalendarSelectionState("day");
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [focusedDate],
    );

    const handleYearSelection = useCallback(
      (year: number | null) => {
        if (year === null) return;
        yearButtonRef.current?.focus();
        setToolbarFocus(toolbarFocusStates[1]);
        setSelectedYear(year);
        setFocusedDate(
          DateTime.fromObject(
            {
              year: year,
              month: focusedDate?.month ?? 1,
              day: 1,
            },
            { zone: defaultTimeZone },
          ).startOf("day"),
        );
        setCalendarSelectionState("day");
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [focusedDate?.month],
    );

    return (
      <CalendarContext.Provider value={providerValue as CalendarContextProps}>
        <div
          ref={combinedRef}
          id={id ?? uid}
          style={styleCombined}
          className={classNameCombined}
          data-anv="calendar"
          {...remainingProps}
        >
          <div className={styles["header"]}>
            <span
              ref={headerRef}
              aria-label="Calendar navigation"
              role="toolbar"
              onKeyDown={handleHeaderKeyDown}
              className={styles["controller"]}
            >
              <CalendarMonthButton
                ref={monthButtonRef}
                tabIndex={
                  _disableFocus
                    ? -1
                    : toolbarFocus === toolbarFocusStates[0]
                      ? 0
                      : -1
                }
                data-calendar-header-button
                data-calendar-header-button-name={toolbarFocusStates[0]}
                calendarSelectionState={calendarSelectionState}
                onClick={() => {
                  setCalendarSelectionState((prev) =>
                    prev === "month" ? "day" : "month",
                  );
                  setToolbarFocus(toolbarFocusStates[0]);
                }}
              />
              <CalendarYearButton
                ref={yearButtonRef}
                tabIndex={
                  _disableFocus
                    ? -1
                    : toolbarFocus === toolbarFocusStates[1]
                      ? 0
                      : -1
                }
                data-calendar-header-button
                data-calendar-header-button-name={toolbarFocusStates[1]}
                calendarSelectionState={calendarSelectionState}
                onClick={() => {
                  setCalendarSelectionState((prev) =>
                    prev === "year" ? "day" : "year",
                  );
                  setToolbarFocus(toolbarFocusStates[1]);
                }}
              />
              {calendarSelectionState === "day" && (
                <>
                  <CalendarPrev
                    tabIndex={
                      _disableFocus
                        ? -1
                        : toolbarFocus === toolbarFocusStates[2]
                          ? 0
                          : -1
                    }
                    data-calendar-header-button
                    data-calendar-header-button-name={toolbarFocusStates[2]}
                    onClick={() => setToolbarFocus(toolbarFocusStates[2])}
                  />
                  <CalendarNext
                    tabIndex={
                      _disableFocus
                        ? -1
                        : toolbarFocus === toolbarFocusStates[3]
                          ? 0
                          : -1
                    }
                    data-calendar-header-button
                    data-calendar-header-button-name={toolbarFocusStates[3]}
                    onClick={() => setToolbarFocus(toolbarFocusStates[3])}
                  />
                  <CalendarNow
                    tabIndex={
                      _disableFocus
                        ? -1
                        : toolbarFocus === toolbarFocusStates[4]
                          ? 0
                          : -1
                    }
                    data-calendar-header-button
                    data-calendar-header-button-name={toolbarFocusStates[4]}
                    onClickFixed={() => setToolbarFocus(toolbarFocusStates[4])}
                  />
                </>
              )}
            </span>
          </div>
          {calendarSelectionState === "day" && <CalendarMonth />}
          {calendarSelectionState === "month" && (
            <CalendarMonthSelection onMonthSelection={handleMonthSelection} />
          )}
          {calendarSelectionState === "year" && (
            // Wrapper div provides some manual offsetting to account for internal padding of the year selector
            <div className={styles["year-selector"]}>
              <YearSelector
                onConfirm={handleYearSelection}
                required
                defaultValue={selectedYear}
                min={normalizedMinDate?.year}
                max={normalizedMaxDate?.year}
              />
            </div>
          )}
        </div>
      </CalendarContext.Provider>
    );
  }),
  {
    /**
     * Calendar.Now component for navigating to today's date.
     *
     * Features:
     * - Quick navigation to current date
     * - Accessible button with proper ARIA labels
     * - Integrates with calendar context
     * - Data tracking integration for analytics
     *
     * @example
     * <Calendar.Now />
     */
    Now: CalendarNow,
    /**
     * Calendar.Month component for displaying the month grid.
     *
     * Features:
     * - Displays calendar days in a table format
     * - Handles day selection and navigation
     * - Supports range selection mode
     * - Accessible with proper ARIA attributes
     * - Data tracking integration for analytics
     *
     * @example
     * <Calendar.Month />
     */
    Month: CalendarMonth,
    /**
     * Calendar.Next component for navigating to the next month.
     *
     * Features:
     * - Navigation to next month
     * - Accessible button with proper ARIA labels
     * - Integrates with calendar context
     * - Data tracking integration for analytics
     *
     * @example
     * <Calendar.Next />
     */
    Next: CalendarNext,
    /**
     * Calendar.Prev component for navigating to the previous month.
     *
     * Features:
     * - Navigation to previous month
     * - Accessible button with proper ARIA labels
     * - Integrates with calendar context
     * - Data tracking integration for analytics
     *
     * @example
     * <Calendar.Prev />
     */
    Prev: CalendarPrev,
    /**
     * Calendar.MonthButton component for switching to month selection view.
     *
     * Features:
     * - Switches calendar to month selection mode
     * - Accessible button with proper ARIA labels
     * - Integrates with calendar context
     * - Data tracking integration for analytics
     *
     * @example
     * <Calendar.MonthButton />
     */
    MonthButton: CalendarMonthButton,
    /**
     * Calendar.YearButton component for switching to year selection view.
     *
     * Features:
     * - Switches calendar to year selection mode
     * - Accessible button with proper ARIA labels
     * - Integrates with calendar context
     * - Data tracking integration for analytics
     *
     * @example
     * <Calendar.YearButton />
     */
    YearButton: CalendarYearButton,
  },
);
Calendar.displayName = "Calendar";
// export const useCalendar = useCoreCalendar;
export const CalendarElement = Calendar;
